import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Container from '@material-ui/core/Container';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

const emptyForm = {
	id: '',
	label: '',
	telegramId: '',
	enabled: true,
};

function getAuthHeaders() {
	return {
		Authorization: localStorage.getItem('ggs_token'),
	};
}

const styles = {
	page: {
		boxSizing: 'border-box',
		background: '#111111',
		color: '#ffffff',
		minHeight: '100vh',
		overflowX: 'hidden',
		paddingTop: 32,
		paddingBottom: 32,
		width: '100%',
	},
	content: {
		boxSizing: 'border-box',
		maxWidth: 1180,
		margin: '0 auto',
		padding: '0 24px',
		width: '100%',
	},
	header: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		marginBottom: 24,
	},
	titleBlock: {
		display: 'grid',
		gap: 4,
	},
	muted: {
		color: 'rgba(255, 255, 255, 0.62)',
	},
	form: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
		gap: 12,
		alignItems: 'center',
		marginBottom: 24,
		padding: 16,
		background: '#171717',
		boxSizing: 'border-box',
		border: '1px solid rgba(255, 255, 255, 0.08)',
		borderRadius: 8,
		boxShadow: '0 18px 50px rgba(0, 0, 0, 0.28)',
		width: '100%',
	},
	tableWrap: {
		background: '#171717',
		boxSizing: 'border-box',
		border: '1px solid rgba(255, 255, 255, 0.08)',
		borderRadius: 8,
		boxShadow: '0 18px 50px rgba(0, 0, 0, 0.28)',
		maxWidth: '100%',
		overflowX: 'auto',
		width: '100%',
	},
	table: {
		minWidth: 980,
	},
	tableHeadCell: {
		borderBottom: '1px solid rgba(253, 142, 50, 0.34)',
		color: '#fd8e32',
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: 0,
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
	},
	tableCell: {
		borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
		color: 'rgba(255, 255, 255, 0.86)',
		whiteSpace: 'nowrap',
	},
	userId: {
		color: '#ffffff',
		fontFamily: 'monospace',
		fontSize: 13,
	},
	input: {
		background: '#111111',
		borderRadius: 4,
	},
	inputText: {
		color: '#ffffff',
	},
	inputLabel: {
		color: 'rgba(255, 255, 255, 0.62)',
	},
	primaryButton: {
		background: '#fd8e32',
		color: '#111111',
		fontWeight: 700,
		height: 40,
		textTransform: 'none',
	},
	secondaryButton: {
		borderColor: 'rgba(253, 142, 50, 0.55)',
		color: '#fd8e32',
		textTransform: 'none',
	},
	iconButton: {
		color: 'rgba(255, 255, 255, 0.72)',
	},
	deleteButton: {
		color: '#ff6b5f',
	},
	error: {
		color: '#fd8e32',
		marginBottom: 16,
	},
};

const AdminUsers = () => {
	const [users, setUsers] = useState([]);
	const [form, setForm] = useState(emptyForm);
	const [error, setError] = useState('');
	const [isLoading, setLoading] = useState(false);

	const loadUsers = async () => {
		setLoading(true);
		setError('');

		try {
			const result = await axios.get('/admin/users', { headers: getAuthHeaders() });
			setUsers(result.data?.users || []);
		} catch (error) {
			setError('Failed to load users.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadUsers();
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');

		const payload = {
			label: form.label,
			telegramId: form.telegramId,
			enabled: form.enabled,
		};

		try {
			if (form.id) {
				await axios.put(`/admin/users/${form.id}`, payload, { headers: getAuthHeaders() });
			} else {
				await axios.post('/admin/users', payload, { headers: getAuthHeaders() });
			}

			setForm(emptyForm);
			loadUsers();
		} catch (error) {
			setError(error.response?.data?.error || 'Failed to save user.');
		}
	};

	const handleEdit = (user) => {
		setForm({
			id: user.id,
			label: user.label,
			telegramId: user.telegramId || '',
			enabled: user.enabled,
		});
	};

	const handleToggleEnabled = async (user) => {
		try {
			await axios.put(
				`/admin/users/${user.id}`,
				{ label: user.label, telegramId: user.telegramId, enabled: !user.enabled },
				{ headers: getAuthHeaders() },
			);
			loadUsers();
		} catch (error) {
			setError(error.response?.data?.error || 'Failed to update user.');
		}
	};

	const handleDelete = async (user) => {
		try {
			await axios.delete(`/admin/users/${user.id}`, { headers: getAuthHeaders() });
			if (form.id === user.id) {
				setForm(emptyForm);
			}
			loadUsers();
		} catch (error) {
			setError(error.response?.data?.error || 'Failed to delete user.');
		}
	};

	return (
		<div style={styles.page}>
		<Container maxWidth={false} style={styles.content}>
			<div style={styles.header}>
				<div style={styles.titleBlock}>
					<Typography variant="h4">User access</Typography>
					<Typography style={styles.muted}>Manage user IDs, Telegram references, and active sessions.</Typography>
				</div>
				<Button href="/" startIcon={<ArrowBackIcon />} style={styles.secondaryButton} variant="outlined">Back</Button>
			</div>

			{error && <Typography style={styles.error}>{error}</Typography>}

			<Paper component="form" style={styles.form} onSubmit={handleSubmit}>
				<TextField
					label="Label"
					value={form.label}
					onChange={(event) => setForm({ ...form, label: event.target.value })}
					variant="outlined"
					size="small"
					style={styles.input}
					InputLabelProps={{ style: styles.inputLabel }}
					InputProps={{ style: styles.inputText }}
				/>
				<TextField
					label="Telegram ID"
					value={form.telegramId}
					onChange={(event) => setForm({ ...form, telegramId: event.target.value })}
					variant="outlined"
					size="small"
					style={styles.input}
					InputLabelProps={{ style: styles.inputLabel }}
					InputProps={{ style: styles.inputText }}
				/>
				<FormControlLabel
					control={(
						<Checkbox
							checked={form.enabled}
							color="primary"
							style={{ color: '#fd8e32' }}
							onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
						/>
					)}
					label="Enabled"
					style={styles.muted}
				/>
				<Button
					disabled={!form.label.trim()}
					startIcon={form.id ? <SaveIcon /> : <AddIcon />}
					style={styles.primaryButton}
					type="submit"
					variant="contained"
				>
					{form.id ? 'Save' : 'Add'}
				</Button>
			</Paper>

			<Paper style={styles.tableWrap}>
				<Table style={styles.table}>
					<TableHead>
						<TableRow>
							<TableCell style={styles.tableHeadCell}>User ID</TableCell>
							<TableCell style={styles.tableHeadCell}>Label</TableCell>
							<TableCell style={styles.tableHeadCell}>Telegram ID</TableCell>
							<TableCell style={styles.tableHeadCell}>Enabled</TableCell>
							<TableCell style={styles.tableHeadCell}>Admin</TableCell>
							<TableCell style={styles.tableHeadCell}>Active sessions</TableCell>
							<TableCell align="right" style={styles.tableHeadCell}>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{users.map((user) => (
							<TableRow key={user.id}>
								<TableCell style={{ ...styles.tableCell, ...styles.userId }}>{user.id}</TableCell>
								<TableCell style={styles.tableCell}>{user.label}</TableCell>
								<TableCell style={styles.tableCell}>{user.telegramId || ''}</TableCell>
								<TableCell style={styles.tableCell}>
									<Checkbox
										checked={user.enabled}
										color="primary"
										style={{ color: '#fd8e32' }}
										onChange={() => handleToggleEnabled(user)}
									/>
								</TableCell>
								<TableCell style={styles.tableCell}>{user.isAdmin ? 'Yes' : 'No'}</TableCell>
								<TableCell style={styles.tableCell}>{user.activeSessions}</TableCell>
								<TableCell align="right" style={styles.tableCell}>
									<IconButton aria-label="edit user" onClick={() => handleEdit(user)} style={styles.iconButton}>
										<EditIcon />
									</IconButton>
									<IconButton aria-label="delete user" onClick={() => handleDelete(user)} style={styles.deleteButton}>
										<DeleteIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
						{!isLoading && users.length === 0 && (
							<TableRow>
								<TableCell colSpan={7} style={styles.tableCell}>No users found.</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</Paper>
		</Container>
		</div>
	);
};

export default AdminUsers;
